import { MaterialOrderPublicView } from '@/components/manufacturing/MaterialOrderPublicView';
import React from "react";
import { SupplierReturnForm } from "@/components/manufacturing/SupplierReturnForm";
import { Toaster } from "@/components/ui/toaster";
import { Header } from "@/components/Header";
import { LoginForm } from "@/components/auth/LoginForm";
import { AuthProvider, useAuth } from "@/components/auth/AuthContext";
import { ColorSettingsProvider } from "@/contexts/ColorSettingsContext";
import { Settings } from "@/components/settings/Settings";
import { ManufacturingDashboard } from "@/components/manufacturing/ManufacturingDashboard";
import { AlfawinSalesStudy } from '@/components/control-panel/AlfawinSalesStudy';
import { AlfawinAccounting } from "@/components/control-panel/AlfawinAccounting";
import { ControlPanel } from "@/components/control-panel/ControlPanel";
import { ProductTransferUtility } from "@/components/utilities/ProductTransferUtility";
import { CustomerSepaList } from "@/components/utilities/CustomerSepaList";
import { ProductStatusUtility } from "@/components/utilities/ProductStatusUtility";
import { SupplierInvoicesWorkspace } from "@/components/payables/SupplierInvoicesWorkspace";

const ROUTES = {
  CONTROL_PANEL: "cuadro-control",
  ARTICLES: "articulos",
  SIMULATIONS: "simulaciones",
  ORDERS: "ordenes-fabricacion",
  SETTINGS: "configuracion",
  MASTER_FAMILIES: "maestros/familias",
  MASTER_GARMENT_TYPES: "maestros/tipos-prenda",
  MASTER_SEASONS: "maestros/temporadas",
  MASTER_PROCESSES: "maestros/procesos",
  MASTER_MATERIAL_REFERENCES: "maestros/referencias-materiales",
  MASTER_MATERIALS: "maestros/materiales",
  MASTER_TEMPLATES: "maestros/plantillas",
  MASTER_SUPPLIERS: "maestros/proveedores",
  MASTER_SUPPLIER_TYPES: "maestros/tipos-proveedores",
  UTILITIES: "utilidades",
  UTILITIES_TRANSFER: "utilidades/traspaso-ventas",
  UTILITIES_CUSTOMERS_SEPA: "utilidades/clientes-sepa",
  UTILITIES_PRODUCT_STATUS: "utilidades/estado-articulos",
  PAYABLES: "facturas-proveedores",
};

const LEGACY_ROUTES = {
  pedidos: ROUTES.SIMULATIONS,
  "facturas-acreedores": ROUTES.PAYABLES,
  manufacturing: ROUTES.ARTICLES,
  settings: ROUTES.SETTINGS,
};

function normalizeRoute(hash) {
  const route = hash.replace(/^#\/?/, "").split('?')[0];
  return LEGACY_ROUTES[route] || route;
}

function NavButton({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-4 py-2 text-sm font-medium transition ${
        active ? "bg-primary text-primary-foreground shadow-sm" : "text-gray-700 hover:bg-gray-100"
      }`}
    >
      {children}
    </button>
  );
}

function AppContent() {
  const [hash, setHash] = React.useState(window.location.hash);
  const { user, hasPermission } = useAuth();

  React.useEffect(() => {
    const handleHashChange = () => {
      setHash(window.location.hash);
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  if (!user) {
    return <LoginForm />;
  }

  const canUseManufacturing = user.role === "admin";
  const canUseSettings = hasPermission("configuracion");
  const requestedRoute = normalizeRoute(hash);
  const defaultRoute = canUseManufacturing ? ROUTES.ARTICLES : ROUTES.SETTINGS;
  const activeRoute = requestedRoute || defaultRoute;
  const mastersActive = activeRoute.startsWith("maestros/");
  const utilitiesActive = activeRoute.startsWith("utilidades");

  const navigate = (route) => {
    window.location.hash = `#/${route}`;
  };

  const renderContent = () => {
    if (!canUseManufacturing && !canUseSettings) {
      return <div>Acceso denegado</div>;
    }

    if (canUseManufacturing) {
      if (activeRoute === `${ROUTES.CONTROL_PANEL}/contabilidad`) {
        return <AlfawinAccounting />;
      }
      if (activeRoute === `${ROUTES.CONTROL_PANEL}/ventas`) {
        return <AlfawinSalesStudy />;
      }
      if (activeRoute === ROUTES.CONTROL_PANEL) {
        return <ControlPanel key="overview" view="overview" />;
      }
      if (activeRoute === `${ROUTES.CONTROL_PANEL}/alfawin`) {
        return <ControlPanel key="alfawin" view="alfawin" />;
      }
      if (activeRoute === `${ROUTES.CONTROL_PANEL}/almacen`) {
        return <ControlPanel key="financial" view="financial" />;
      }
      if (activeRoute === `${ROUTES.CONTROL_PANEL}/fabricaciones`) {
        return <ControlPanel key="manufacturing" view="manufacturing" />;
      }
      if (activeRoute === ROUTES.ARTICLES) {
        return <ManufacturingDashboard view="articles" />;
      }
      if (activeRoute === ROUTES.SIMULATIONS) {
        return <ManufacturingDashboard view="simulations" />;
      }
      if (activeRoute === ROUTES.ORDERS) {
        return <ManufacturingDashboard view="orders" />;
      }
      if (activeRoute === ROUTES.PAYABLES) {
        return <SupplierInvoicesWorkspace />;
      }
      if (activeRoute === ROUTES.MASTER_FAMILIES) {
        return <ManufacturingDashboard view="families" />;
      }
      if (activeRoute === ROUTES.MASTER_GARMENT_TYPES) return <ManufacturingDashboard view="garment-types" />;
      if (activeRoute === ROUTES.MASTER_SEASONS) {
        return <ManufacturingDashboard view="seasons" />;
      }
      if (activeRoute === ROUTES.MASTER_PROCESSES) {
        return <ManufacturingDashboard view="processes" />;
      }
      if (activeRoute === ROUTES.MASTER_MATERIAL_REFERENCES) return <ManufacturingDashboard view="material-references" />;
      if (activeRoute === ROUTES.MASTER_MATERIALS) {
        return <ManufacturingDashboard view="materials" />;
      }
      if (activeRoute === ROUTES.MASTER_TEMPLATES) {
        return <ManufacturingDashboard view="templates" />;
      }
      if (activeRoute === ROUTES.MASTER_SUPPLIERS) {
        return <ManufacturingDashboard view="suppliers" />;
      }
      if (activeRoute === ROUTES.MASTER_SUPPLIER_TYPES) {
        return <ManufacturingDashboard view="supplier-types" />;
      }
      if (activeRoute === ROUTES.UTILITIES || activeRoute === ROUTES.UTILITIES_TRANSFER) {
        return <ProductTransferUtility />;
      }
      if (activeRoute === ROUTES.UTILITIES_CUSTOMERS_SEPA) {
        return <CustomerSepaList />;
      }
      if (activeRoute === ROUTES.UTILITIES_PRODUCT_STATUS) {
        return <ProductStatusUtility />;
      }
    }

    if (canUseSettings && activeRoute === ROUTES.SETTINGS) {
      return <Settings />;
    }

    return canUseManufacturing ? <ManufacturingDashboard view="articles" /> : <Settings />;
  };

  if (canUseManufacturing && activeRoute === ROUTES.CONTROL_PANEL) {
    return <ColorSettingsProvider><ControlPanel key="overview" view="overview" /></ColorSettingsProvider>;
  }

  return (
    <ColorSettingsProvider>
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
        <Header />
        <main className="container mx-auto p-4">
          <nav className="mb-6 flex flex-wrap items-center gap-2 rounded-lg border bg-white p-2 shadow-sm">
            {canUseManufacturing && (
              <>
                <NavButton active={activeRoute.startsWith(ROUTES.CONTROL_PANEL)} onClick={() => navigate(ROUTES.CONTROL_PANEL)}>
                  Cuadro de control
                </NavButton>
                <div className="group relative">
                  <NavButton active={mastersActive} onClick={() => navigate(ROUTES.MASTER_PROCESSES)}>
                    Maestros
                  </NavButton>
                  <div className="absolute left-0 z-20 hidden min-w-48 flex-col rounded-md border bg-white p-1 shadow-lg group-focus-within:flex group-hover:flex">
                    <button
                      type="button"
                      onClick={() => navigate(ROUTES.MASTER_FAMILIES)}
                      className="rounded px-3 py-2 text-left text-sm hover:bg-gray-100"
                    >
                      Familias
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(ROUTES.MASTER_SEASONS)}
                      className="rounded px-3 py-2 text-left text-sm hover:bg-gray-100"
                    >
                      Temporadas
                    </button>
                    <button type="button" onClick={() => navigate(ROUTES.MASTER_GARMENT_TYPES)} className="rounded px-3 py-2 text-left text-sm hover:bg-gray-100">Tipos de prenda</button>
                    <button
                      type="button"
                      onClick={() => navigate(ROUTES.MASTER_PROCESSES)}
                      className="rounded px-3 py-2 text-left text-sm hover:bg-gray-100"
                    >
                      Procesos
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(ROUTES.MASTER_MATERIALS)}
                      className="rounded px-3 py-2 text-left text-sm hover:bg-gray-100"
                    >
                      Materiales
                    </button>
                    <button type="button" onClick={() => navigate(ROUTES.MASTER_MATERIAL_REFERENCES)} className="rounded px-3 py-2 text-left text-sm hover:bg-gray-100">Referencias de materiales</button>
                    <button
                      type="button"
                      onClick={() => navigate(ROUTES.MASTER_TEMPLATES)}
                      className="rounded px-3 py-2 text-left text-sm hover:bg-gray-100"
                    >
                      Plantillas
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(ROUTES.MASTER_SUPPLIERS)}
                      className="rounded px-3 py-2 text-left text-sm hover:bg-gray-100"
                    >
                      Proveedores
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(ROUTES.MASTER_SUPPLIER_TYPES)}
                      className="rounded px-3 py-2 text-left text-sm hover:bg-gray-100"
                    >
                      Tipos de proveedores
                    </button>
                  </div>
                </div>
                <NavButton active={activeRoute === ROUTES.ARTICLES} onClick={() => navigate(ROUTES.ARTICLES)}>
                  Artículos
                </NavButton>
                <NavButton active={activeRoute === ROUTES.SIMULATIONS} onClick={() => navigate(ROUTES.SIMULATIONS)}>
                  Simulaciones
                </NavButton>
                <NavButton active={activeRoute === ROUTES.ORDERS} onClick={() => navigate(ROUTES.ORDERS)}>
                  Órdenes fabricación
                </NavButton>
                <NavButton active={activeRoute === ROUTES.PAYABLES} onClick={() => navigate(ROUTES.PAYABLES)}>
                  Facturas de proveedores
                </NavButton>
                <div className="group relative">
                  <NavButton active={utilitiesActive} onClick={() => navigate(ROUTES.UTILITIES_TRANSFER)}>
                    Utilidades
                  </NavButton>
                  <div className="absolute left-0 z-20 hidden min-w-48 flex-col rounded-md border bg-white p-1 shadow-lg group-focus-within:flex group-hover:flex">
                    <button
                      type="button"
                      onClick={() => navigate(ROUTES.UTILITIES_TRANSFER)}
                      className="rounded px-3 py-2 text-left text-sm hover:bg-gray-100"
                    >
                      Traspaso a ventas
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(ROUTES.UTILITIES_CUSTOMERS_SEPA)}
                      className="rounded px-3 py-2 text-left text-sm hover:bg-gray-100"
                    >
                      Listado clientes (SEPA)
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(ROUTES.UTILITIES_PRODUCT_STATUS)}
                      className="rounded px-3 py-2 text-left text-sm hover:bg-gray-100"
                    >
                      Estado de artículos
                    </button>
                  </div>
                </div>
              </>
            )}
            {canUseSettings && (
              <NavButton active={activeRoute === ROUTES.SETTINGS} onClick={() => navigate(ROUTES.SETTINGS)}>
                Configuración
              </NavButton>
            )}
          </nav>
          {renderContent()}
        </main>
      </div>
    </ColorSettingsProvider>
  );
}

export default function App() {
  const [publicHash, setPublicHash] = React.useState(window.location.hash);
  React.useEffect(() => {
    const update = () => setPublicHash(window.location.hash);
    window.addEventListener('hashchange', update);
    return () => window.removeEventListener('hashchange', update);
  }, []);
  if (publicHash.startsWith('#/pedido-material/')) return <MaterialOrderPublicView key={publicHash} token={publicHash.slice('#/pedido-material/'.length)} />;
  if (publicHash.startsWith('#/devolucion/')) return <SupplierReturnForm key={publicHash} token={publicHash.slice('#/devolucion/'.length)} />;
  return (
    <>
      <Toaster />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </>
  );
}
